import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { useGetMyUserReports } from "@/services-react-query/queries/user-report.query";
import { IUserReport, UserReportStatus, UserReportType } from "@/common/types/user-report";

export const MyTicketList: React.FC = () => {
  const { data, isLoading, refetch, isRefetching } = useGetMyUserReports(1, 20);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  const getStatusBadge = (status: UserReportStatus) => {
    switch (status) {
      case UserReportStatus.PENDING:
        return (
          <Chip
            size="small"
            icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
            label="Chờ tiếp nhận"
            sx={{
              backgroundColor: "rgba(245, 158, 11, 0.12)",
              color: "#D97706",
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        );
      case UserReportStatus.IN_PROGRESS:
        return (
          <Chip
            size="small"
            icon={<BuildCircleOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Đang xử lý"
            sx={{
              backgroundColor: "rgba(59, 130, 246, 0.12)",
              color: "#2563EB",
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        );
      case UserReportStatus.RESOLVED:
        return (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
            label="Đã giải quyết"
            sx={{
              backgroundColor: "rgba(16, 185, 129, 0.12)",
              color: "#059669",
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        );
      case UserReportStatus.REJECTED:
        return (
          <Chip
            size="small"
            icon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Từ chối"
            sx={{
              backgroundColor: "rgba(239, 68, 68, 0.12)",
              color: "#DC2626",
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        );
      case UserReportStatus.CLOSED:
        return (
          <Chip
            size="small"
            icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Đã đóng"
            sx={{
              backgroundColor: "rgba(100, 116, 139, 0.12)",
              color: "#475569",
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        );
      default:
        return <Chip size="small" label={status} />;
    }
  };

  const getTypeLabel = (type: UserReportType) => {
    switch (type) {
      case UserReportType.BUG_TECHNICAL:
        return "Lỗi kỹ thuật";
      case UserReportType.AI_CONTENT:
        return "Nội dung / AI";
      case UserReportType.FEATURE_REQUEST:
        return "Đề xuất tính năng";
      default:
        return "Khác";
    }
  };

  const getReportsList = (): IUserReport[] => {
    if (!data) return [];
    if (Array.isArray((data as any)?.data?.data)) return (data as any).data.data;
    if (Array.isArray((data as any)?.data)) return (data as any).data;
    if (Array.isArray(data)) return data as any;
    return [];
  };

  const reports: IUserReport[] = getReportsList();

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
        <CircularProgress size={32} />
        <Typography variant="caption" color="text.secondary" mt={2}>
          Đang tải lịch sử báo cáo...
        </Typography>
      </Box>
    );
  }

  if (reports.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} textAlign="center">
        <Typography variant="body1" fontWeight={700} color="text.primary" mb={1}>
          Bạn chưa gửi báo cáo sự cố nào
        </Typography>
        <Typography variant="caption" color="text.secondary" maxWidth={360}>
          Nếu bạn gặp bất kỳ vấn đề gì trong lúc học tập hoặc muốn góp ý tính năng, hãy gửi phản hồi ở tab "Gửi báo cáo mới".
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle2" color="text.secondary">
          Tổng cộng: <strong>{reports.length}</strong> báo cáo
        </Typography>
        <Tooltip title="Làm mới danh sách">
          <IconButton size="small" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshIcon fontSize="small" sx={{ animation: isRefetching ? "spin 1s infinite" : "none" }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box display="flex" flexDirection="column" gap={2} sx={{ maxHeight: 420, overflowY: "auto", pr: 0.5 }}>
        {reports.map((report) => (
          <Box
            key={report._id}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              transition: "border-color 0.2s",
              "&:hover": { borderColor: "primary.main" },
            }}
          >
            {/* Header: Ticket code + Type + Status */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                  #{report.ticketCode}
                </Typography>
                <Chip
                  label={getTypeLabel(report.type)}
                  size="small"
                  sx={{ fontSize: 10, height: 20, fontWeight: 600 }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                {report.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(report.createdAt).toLocaleString("vi-VN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                )}
                {getStatusBadge(report.status)}
              </Box>
            </Box>

            {/* Title & Description */}
            <Typography variant="body2" fontWeight={700} color="text.primary" mb={0.5}>
              {report.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", fontSize: 13, mb: 1.5 }}
            >
              {report.description}
            </Typography>

            {/* Audio attached */}
            {report.audioUrl && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                p={1}
                mb={1.5}
                borderRadius={1.5}
                sx={{ backgroundColor: "action.hover" }}
              >
                <GraphicEqIcon color="primary" fontSize="small" />
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Ghi âm giọng nói ({report.audioDuration || 0}s):
                </Typography>
                <audio controls src={report.audioUrl} style={{ height: 28, maxWidth: "100%" }} />
              </Box>
            )}

            {/* Images attached */}
            {report.images && report.images.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                {report.images.map((imgUrl, imgIdx) => (
                  <Box
                    key={imgIdx}
                    onClick={() => setSelectedPreviewImage(imgUrl)}
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    <img
                      src={imgUrl}
                      alt={`attachment-${imgIdx}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Admin Response Box */}
            {report.adminResponse && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "rgba(16, 185, 129, 0.08)",
                  borderLeft: "3px solid #10B981",
                }}
              >
                <Typography variant="caption" fontWeight={800} color="#047857" display="block" mb={0.3}>
                  💬 Phản hồi từ Quản trị viên:
                </Typography>
                <Typography variant="body2" color="#065F46" sx={{ fontSize: 13 }}>
                  {report.adminResponse}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Modal phóng to ảnh */}
      <Dialog
        open={!!selectedPreviewImage}
        onClose={() => setSelectedPreviewImage(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ p: 1 }}>
          {selectedPreviewImage && (
            <img
              src={selectedPreviewImage}
              alt="Report Screenshot Full"
              style={{ width: "100%", height: "auto", borderRadius: 8 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
